import { Plane } from "lucide-react";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";

/**
 * Optional flight details panel.
 * Props:
 *   value: { flightNumber, departureDatetime, arrivalDatetime } | null
 *   onChange: (next) => void  // pass null to disable
 */
export default function FlightDetailsFields({ value, onChange }) {
  const enabled = !!value;
  const v = value || { flightNumber: "", departureDatetime: "", arrivalDatetime: "" };

  const toggle = (on) => {
    if (on) onChange({ flightNumber: "", departureDatetime: "", arrivalDatetime: "" });
    else onChange(null);
  };

  const update = (field, val) => onChange({ ...v, [field]: val });

  return (
    <div className="bg-[#0F0F0F]/60 border border-white/5 rounded-sm p-4" data-testid="flight-details-section">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-white/80 text-sm">
          <Plane className="w-4 h-4 text-[#C5A059]" /> Flight details (optional)
        </div>
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="flight-details-toggle" />
      </div>

      {enabled && (
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-white/40 text-[10px] uppercase mb-1 block">Flight number</label>
            <Input
              value={v.flightNumber}
              onChange={(e) => update("flightNumber", e.target.value)}
              placeholder="e.g. AZ1234"
              className="input-dark"
              data-testid="flight-number-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase mb-1 block">Departure (origin) date &amp; time</label>
              <Input
                type="datetime-local"
                value={v.departureDatetime}
                onChange={(e) => update("departureDatetime", e.target.value)}
                className="input-dark"
                data-testid="flight-departure-input"
              />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase mb-1 block">Arrival (Venice) date &amp; time</label>
              <Input
                type="datetime-local"
                value={v.arrivalDatetime}
                onChange={(e) => update("arrivalDatetime", e.target.value)}
                className="input-dark"
                data-testid="flight-arrival-input"
              />
            </div>
          </div>
          <p className="text-white/30 text-[11px] italic">Sharing your flight helps us track delays and adjust pickup time automatically.</p>
        </div>
      )}
    </div>
  );
}

/** Build the API payload for flight details, returning null if nothing meaningful */
export function buildFlightDetailsPayload(value) {
  if (!value) return null;
  const { flightNumber, departureDatetime, arrivalDatetime } = value;
  if (!flightNumber && !departureDatetime && !arrivalDatetime) return null;
  return {
    flight_number: flightNumber || null,
    departure_datetime: departureDatetime || null,
    arrival_datetime: arrivalDatetime || null
  };
}
